import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { AdaptationCycleHeader } from '../components/AdaptationCycleHeader'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import HomeContent from '../../../../src/components/SectionContent'
import { Check, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

import { ADAPTATION_CYCLE_MAP } from '../config/adaptationCycle.config'
import { adaptationCycleOverview } from '../config/adaptation-cycle-overview.config'
import { AdaptationCycleActivity, AdaptationCycleStageConfig } from '../types'
import Callout from '../components/Callout'
import { Button } from '@/components/ui/button'
import { appLink } from '@app/config/appLink'
import ArticleAccordion from '../components/ArticleAccordion'

export const AdaptationCycleTemplatePage = () => {
  const navigate = useNavigate()
  const { adaptationCycleId } = useParams<{ adaptationCycleId: string }>()
  const [searchParams] = useSearchParams()

  const adaptationCycleStage: AdaptationCycleStageConfig | null = adaptationCycleId
    ? ADAPTATION_CYCLE_MAP[adaptationCycleId]
    : null

  if (!adaptationCycleStage) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <p className="text-muted-foreground">
          Adaptation cycle not found: <span className="font-mono">{adaptationCycleId}</span>
        </p>
        <button
          onClick={() => navigate(appLink.homeWelcome.path)}
          className="text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Back to welcome
        </button>
      </div>
    )
  }

  const { outcome, topParagraphs, activities } = adaptationCycleStage

  const [activeTab, setActiveTab] = useState(activities[0].activity)

  useEffect(() => {
    const requestedActivity = searchParams.get('activity')
    const requestedActivityExists = activities.some(
      (activity) => activity.activity === requestedActivity,
    )
    setActiveTab(requestedActivityExists ? (requestedActivity as string) : activities[0].activity)
  }, [adaptationCycleId, activities, searchParams])

  const relatedActivityHref = (related: { stageId: string; activity?: string }) =>
    `/app/home/adaptation-cycle/${related.stageId}${
      related.activity ? `?activity=${encodeURIComponent(related.activity)}` : ''
    }`

  const forumExamplesUrl = (activity: AdaptationCycleActivity) => {
    const number = activity.activity.replace(/^Activity\s*/, '').replace('.', '-')
    return `https://forum.resilience-explorer.com/t/adaptation-cycle-activity-${number}-examples-${activity.forumSlug}`
  }

  return (
    <div>
      <HomeContent className="pt-6">
        <Button
          variant={'ghost'}
          onClick={() => navigate(`/app/home/adaptation-cycle/${adaptationCycleOverview.id}`)}
        >
          <ChevronLeft />
          Back to Overview
        </Button>
      </HomeContent>
      <AdaptationCycleHeader
        adaptationCycle={adaptationCycleStage}
        children={
          <div>
            <div className="bg-sidebar-accent p-6 rounded-br-[32px] mb-6">
              <h5 className="text-h4 text-brand-navy pb-1">{outcome.title}</h5>
              <p className="text-sm">{outcome.description}</p>
            </div>
            {topParagraphs.map((paragraph, index) => (
              <p className="text-sm pb-4" key={index}>
                {paragraph}
              </p>
            ))}
          </div>
        }
      />
      <div className="border-b border-b-border border-solid w-full" />

      {/* Tabs Content */}
      <div className="mt-6">
        <Tabs className="w-full" value={activeTab} onValueChange={setActiveTab}>
          <HomeContent>
            <TabsList>
              {activities.map((item, index) => (
                <TabsTrigger key={index} value={item.activity}>
                  {item.activity}
                </TabsTrigger>
              ))}
            </TabsList>
          </HomeContent>
          {activities.map((activity, index) => (
            <TabsContent className="text-sm w-full mt-6" value={activity.activity} key={index}>
              <HomeContent>
                <h4 className="text-h3 font-semibold text-primary">
                  {activity.activity.replace(/^Activity\s*/, '')} {activity.title}
                </h4>
                <p className="text-sm mb-4">{activity.description}</p>
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Alignment with 10-Step Cycle:
                  </span>
                  {activity.mfeAlignment.map((alignment, index) => (
                    <Badge key={index} variant="secondary">
                      Step {alignment.step} · {alignment.label}
                    </Badge>
                  ))}
                </div>
              </HomeContent>

              {/* Streams */}
              <div className="bg-sidebar-primary-foreground p-8 mt-6">
                <HomeContent>
                  <h4 className="text-[18px] font-semibold text-primary">Streams</h4>
                  <div className="flex gap-6 mt-4">
                    {activity.streams.map((stream, index) => (
                      <div key={stream.title + index} className="w-1/3">
                        <div className="flex gap-1">
                          <stream.icon className="h-6 w-6 text-[#A3A3A3]" />
                          <h5 className="text-h4 font-semibold">{stream.title}</h5>
                        </div>
                        <ul className="mt-2">
                          {stream.points.map((point, index) => (
                            <li key={index} className="text-sm list-disc ml-6">
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </HomeContent>
              </div>

              {/* Outputs */}
              <HomeContent className="my-8 flex gap-6">
                <div className="w-1/3">
                  <h4 className="text-h3 font-semibold text-primary">Outputs</h4>
                  <ul className="flex mt-4 flex-col">
                    {activity.outputs.map((output, index) => (
                      <li key={index} className="text-sm list-disc ml-6">
                        <p>{output.text}</p>
                        {output.link && (
                          <a
                            className="text-primary underline"
                            target="_blank"
                            rel="noreferrer"
                            href={output.link.href}
                          >
                            {output.link.text}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                  <Button asChild variant="outline" size="sm" className="mt-4">
                    <a
                      href={forumExamplesUrl(activity)}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`See and share examples of ${activity.title} on the Forum (opens in a new tab)`}
                    >
                      See & share examples on Forum
                      <ExternalLink />
                    </a>
                  </Button>
                </div>
                <div className="w-1/3">
                  <h4 className="text-h3 font-semibold text-primary">Connections</h4>
                  <ul className="mt-4">
                    {activity.connections.map((connection, index) => (
                      <li key={index} className="text-sm list-disc ml-6">
                        <p>{connection}</p>
                      </li>
                    ))}
                  </ul>
                  {activity.relatedActivities.length > 0 && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-4">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Related:
                      </span>
                      {activity.relatedActivities.map((related, index) => (
                        <Link
                          key={index}
                          to={relatedActivityHref(related)}
                          className="text-sm text-primary underline underline-offset-4 hover:no-underline"
                        >
                          {related.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-sidebar-accent p-6 rounded-br-[32px] w-1/3">
                  <h4 className="text-h3 font-semibold text-brand-navy">
                    Move to the next activity when...
                  </h4>
                  <ul className="mt-2">
                    {activity.moveOnWhen.map((moveOnText, index) => (
                      <li className="list-none flex" key={index}>
                        <div className="h-6 w-6">
                          <Check className="w-6 h-6 pr-2" />
                        </div>
                        <p className="text-sm">{moveOnText}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </HomeContent>

              <div className="border-b border-b-border border-solid w-full" />

              <HomeContent className="mt-8">
                {/* Resources */}
                <h4 className="text-h3 font-semibold text-primary mb-2">Resources</h4>

                <ArticleAccordion articleGroups={activity.resources} />
              </HomeContent>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <HomeContent>
        {/* Prev/ Next button group */}
        <div className="flex mt-8 justify-end gap-6">
          <Button
            variant={'ghost'}
            disabled={!adaptationCycleStage.previousStage}
            onClick={() =>
              navigate(`/app/home/adaptation-cycle/${adaptationCycleStage.previousStage}`)
            }
          >
            <span>
              <ChevronLeft />
            </span>
            Previous Stage
          </Button>
          <Button
            variant={'outline'}
            disabled={!adaptationCycleStage.nextStage}
            onClick={() => navigate(`/app/home/adaptation-cycle/${adaptationCycleStage.nextStage}`)}
          >
            Next Stage
            <span>
              <ChevronRight />
            </span>
          </Button>
        </div>

        {/* Forum Callout */}
        <div className="max-w-full mb-5 mt-6">
          <Callout
            actionLabel="Visit Forum"
            description="Visit Forum to share lessons and learn best practice from adaptation professionals around the world."
            onActionClick={() =>
              window.open(
                'https://forum.resilience-explorer.com/c/adaptation',
                '_blank',
                'noopener,noreferrer',
              )
            }
            title="Learn how others are adapting."
          />
        </div>
      </HomeContent>
    </div>
  )
}
